import React from "react";
import { useNavigate } from "react-router-dom";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { SourceBadge } from "@/components/SourceBadge";
import { formatDate, formatKr, daysLabel } from "@/lib/format";
import { MoreHorizontal, ArrowUpDown, ShieldAlert, ExternalLink, CheckCircle2, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";

export const InvoiceTable = ({ items, categories = {}, onMarkPaid, onDelete, sort, order, onSort, compact = false }) => {
  const navigate = useNavigate();

  const SortHead = ({ field, children, className }) => (
    <TableHead className={className}>
      {onSort ? (
        <button type="button" onClick={() => onSort(field)} className="inline-flex items-center gap-1 hover:text-foreground" data-testid={`invoice-sort-${field}`}>
          {children}
          <ArrowUpDown className={cn("h-3.5 w-3.5", sort === field ? "text-primary" : "text-muted-foreground/60")} />
        </button>
      ) : (
        children
      )}
    </TableHead>
  );

  return (
    <div className="rounded-xl border bg-card overflow-hidden">
      <Table data-testid="invoice-table">
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <SortHead field="issuer_name">Leverandør</SortHead>
            <SortHead field="due_date" className="hidden sm:table-cell">Forfallsdato</SortHead>
            <SortHead field="amount" className="text-right">Beløp</SortHead>
            <TableHead>Status</TableHead>
            {!compact && <TableHead className="hidden lg:table-cell">Kilde</TableHead>}
            {!compact && <TableHead className="hidden xl:table-cell">Kategori</TableHead>}
            <TableHead className="w-12"><span className="sr-only">Handlinger</span></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((inv) => (
            <TableRow
              key={inv.id}
              className="cursor-pointer row-hover h-14"
              onClick={() => navigate(`/fakturaer/${inv.id}`)}
              data-testid={`invoice-row-${inv.id}`}
            >
              <TableCell className="font-medium">
                <div className="flex items-center gap-2 min-w-0">
                  {inv.flags?.length ? <ShieldAlert className="h-4 w-4 text-flag shrink-0" aria-label="Advarsel" /> : null}
                  <div className="min-w-0">
                    <p className="truncate max-w-[220px] sm:max-w-[320px]">{inv.issuer_name || <span className="text-muted-foreground italic">Ukjent avsender</span>}</p>
                    <p className="text-xs text-muted-foreground sm:hidden">{formatDate(inv.due_date)}</p>
                    {inv.invoice_number ? <p className="text-xs text-muted-foreground hidden sm:block">Faktura {inv.invoice_number}</p> : null}
                  </div>
                </div>
              </TableCell>
              <TableCell className="hidden sm:table-cell tnum">
                <p>{formatDate(inv.due_date)}</p>
                {inv.days_to_due !== null && inv.status !== "betalt" && inv.status !== "avvist" ? <p className="text-xs text-muted-foreground">{daysLabel(inv.days_to_due)}</p> : null}
              </TableCell>
              <TableCell className="text-right tnum font-medium whitespace-nowrap">{formatKr(inv.amount, inv.currency)}</TableCell>
              <TableCell><StatusBadge status={inv.display_status || inv.status} /></TableCell>
              {!compact && <TableCell className="hidden lg:table-cell"><SourceBadge source={inv.source} /></TableCell>}
              {!compact && (
                <TableCell className="hidden xl:table-cell text-sm text-muted-foreground">
                  {inv.category_id && categories[inv.category_id] ? (
                    <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: categories[inv.category_id].color }} />{categories[inv.category_id].name}</span>
                  ) : "–"}
                </TableCell>
              )}
              <TableCell onClick={(e) => e.stopPropagation()}>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" aria-label="Handlinger" data-testid={`invoice-row-menu-${inv.id}`}><MoreHorizontal className="h-4 w-4" /></Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-card">
                    <DropdownMenuItem onClick={() => navigate(`/fakturaer/${inv.id}`)}><ExternalLink className="h-4 w-4 mr-2" /> Åpne</DropdownMenuItem>
                    {inv.status !== "betalt" && inv.status !== "avvist" && onMarkPaid ? (
                      <DropdownMenuItem onClick={() => onMarkPaid(inv)} data-testid={`invoice-row-mark-paid-${inv.id}`}><CheckCircle2 className="h-4 w-4 mr-2" /> Marker som betalt</DropdownMenuItem>
                    ) : null}
                    {onDelete ? (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => onDelete(inv)} data-testid={`invoice-row-delete-${inv.id}`}><Trash2 className="h-4 w-4 mr-2" /> Slett</DropdownMenuItem>
                      </>
                    ) : null}
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
